export const PROCUREMENT_READ_PERMISSION = 'stock.procurement.read';
export const PROCUREMENT_WRITE_PERMISSION = 'stock.procurement.write';

export type ProcurementAccessSubject = {
  role?: string | null;
  roles?: readonly string[] | null;
  permissions?: readonly string[] | null;
  partner_org_id?: string | null;
  partner_role?: string | null;
};

const normalize = (value: unknown): string =>
  String(value || '').trim().toLowerCase().replace(/:/g, '.');

const rolesOf = (subject: ProcurementAccessSubject): Set<string> => {
  const roles = new Set((subject.roles || []).map(normalize).filter(Boolean));
  const primaryRole = normalize(subject.role);
  if (primaryRole) roles.add(primaryRole);
  return roles;
};

const permissionMatches = (grant: string, required: string): boolean => {
  if (grant === '*' || grant === '*.*' || grant === required) return true;
  return grant.endsWith('.*') && required.startsWith(grant.slice(0, -1));
};

const canAccess = (
  subject: ProcurementAccessSubject | null | undefined,
  required: string
): boolean => {
  if (!subject) return false;

  const roles = rolesOf(subject);
  const isPartner =
    [...roles].some(
      (role) => role === 'partner' || role.startsWith('partner_')
    ) ||
    Boolean(normalize(subject.partner_role)) ||
    Boolean(normalize(subject.partner_org_id));
  if (isPartner) return false;
  if (roles.has('admin') || roles.has('super_admin')) return true;

  const accepted =
    required === PROCUREMENT_READ_PERMISSION
      ? [PROCUREMENT_READ_PERMISSION, PROCUREMENT_WRITE_PERMISSION]
      : [PROCUREMENT_WRITE_PERMISSION];
  const grants = (subject.permissions || []).map(normalize).filter(Boolean);
  return grants.some((grant) =>
    accepted.some((permission) => permissionMatches(grant, permission))
  );
};

/** FLOW[49426]: frontend mirrors the backend procurement boundary fail-closed. */
export const canReadProcurement = (
  subject: ProcurementAccessSubject | null | undefined
): boolean => canAccess(subject, PROCUREMENT_READ_PERMISSION);

export const canWriteProcurement = (
  subject: ProcurementAccessSubject | null | undefined
): boolean => canAccess(subject, PROCUREMENT_WRITE_PERMISSION);
